import api from 'api';
import axios from 'axios';
import { StatsList } from 'components/elements';
import Spinner from 'components/elements/loaders/spinner';
import { Section, SectionMessage } from 'components/general-styles';
import { FC, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';

import { MixpanelWrapperInstance } from 'services/mixpanel/mixpanel-wrapper';
import { ClickElementTypes } from 'services/mixpanel/mixpanel.types';
import { EventNames } from 'services/mixpanel/provider-app-mixpanel-events';
import { debug, getImpersonationParams, percentageFormat, weightFormat } from 'utils/helpers';

const PatientsHighLevelStats: FC<RouteComponentProps> = ({ history }) => {
  const [stats, setStats] = useState<
    {
      value: number | string;
      format?: (source: number, decimals: number) => void;
      icon: string;
      label: string;
      onClick?: () => void;
      link?: {
        to: string;
        text: string;
        can?: { I: string; on: string };
        onclick?: void;
      };
      isVisible: boolean;
    }[]
  >([]);
  const [loading, setLoading] = useState(true);

  const processStatsToRender = (data: {
    total_patients: number;
    weight_loss_percent: number;
    weight_loss_total: number;
  }) => {
    const statsList = [
      {
        value: data.total_patients,
        icon: 'user',
        label: 'Total Patients',
        onClick: () => history.push('/patients_statuses'),
        link: {
          to: '/members',
          text: 'View Profiles',
          can: { I: 'enter', on: 'Patients' },
          onclick: MixpanelWrapperInstance.track(EventNames.HomeChangeSection, {
            Section: 'Patients',
          }),
        },
        isVisible: true,
      },
      {
        value: data.weight_loss_percent,
        format: percentageFormat,
        icon: 'decreaseChart',
        label: 'Average Weight Loss',
        onClick: () => {
          history.push('/wl_stats');
          MixpanelWrapperInstance.trackClick({
            customEventName: EventNames.HomeClickInitialDashboard,
            name: 'Average Weight Loss',
            type: ClickElementTypes.IMAGE,
          });
        },
        isVisible: true,
      },
      {
        value: data.weight_loss_total,
        format: weightFormat,
        icon: 'weightScale',
        label: 'Total Weight Loss',
        isVisible: true,
      },
    ].filter((stat) => stat.isVisible);

    setStats(statsList);
  };

  useEffect(() => {
    const source = axios.CancelToken.source();

    const getStats = async () => {
      try {
        const { data } = await api().get<{
          total_patients: number;
          weight_loss_total: number;
          weight_loss_percent: number;
        }>('stats/provider/AUTH_ID/AUTH_TOKEN', {
          cancelToken: source.token,
          // Forward the impersonator during an impersonated session so core's
          // RBAC scopes the dashboard stats to the impersonator's clinics, not
          // the impersonated role (e.g. Tech Support). No-op otherwise —
          // getImpersonationParams() returns {} when not impersonating.
          params: { ...getImpersonationParams() },
        });

        processStatsToRender(data);
        setLoading(false);
      } catch (err) {
        if (!axios.isCancel(err)) {
          debug(err as string);
          setLoading(false);
        }
      }
    };

    getStats();

    return () => {
      source.cancel();
    };
  }, []);

  return (
    <Section>
      {loading ? (
        <SectionMessage>
          <p>Loading stats</p>
          <Spinner size={20} />
        </SectionMessage>
      ) : stats.length > 0 ? (
        <StatsList data={stats} bordered />
      ) : (
        <SectionMessage>
          <p>There are no stats available.</p>
        </SectionMessage>
      )}
    </Section>
  );
};

export default PatientsHighLevelStats;
